<template>
  <div
    class="VisionRating"
    :class="[
      `VisionRating--height-${props.height}`,
      `VisionRating--style-${props.visualStyle}`,
      `VisionRating--theme-${props.theme}`,
      `VisionRating--colour-${props.colour}`,
    ]"
  >
    <div
      v-if="props.visualStyle === 'line'"
      class="VisionRating__line-inner"
      :style="{ '--rating-width': `${computedLineWidth}%` }"
    />
    <div
      v-else-if="props.visualStyle === 'circle'"
      class="VisionRating__circle-container"
      :style="{ '--circle-rating': `${props.rating}` }"
    >
      <div class="VisionRating__icon-container">
        <VisionIcon
          :icon="props.icon"
          :colour="props.colour"
          size="stretch"
        />
      </div>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 40 40"
        class="VisionRating__circle"
      >
        <circle
          cx="20"
          cy="20"
          r="15.91549430918954"
          class="VisionRating__circle-background"
        />
        <circle
          cx="20"
          cy="20"
          class="VisionRating__circle-gradient-stroke"
          r="15.91549430918954"
        />
        <defs>
          <linearGradient
            :id="`VisionRating__linear-${props.theme}-${props.id}`"
            x1="0%"
            y1="0%"
            x2="50%"
            y2="50%"
            gradientTransform="rotate(-65)"
          >
            <stop
              offset="0%"
              stop-color="var(--vision-rating-gradient-stop-one)"
            />
            <stop
              offset="60%"
              stop-color="var(--vision-rating-gradient-stop-two)"
            />
            <stop
              offset="100%"
              stop-color="var(--vision-rating-gradient-stop-three)"
            />
          </linearGradient>
        </defs>
      </svg>
    </div>
    <div class="VisionRating__text">
      <span
        v-if="props.visualStyle === 'line'"
        class="VisionRating__title"
      >
        {{ props.title }}
      </span>
      <span class="VisionRating__rating">{{ props.rating }}/10</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VisionIcon from '../VisionIcon/VisionIcon.vue'
import type { IconName } from '../../config/icons.config'

interface RatingProps {
  id: string
  rating: number
  height?: 'thin' | 'regular'
  theme?: 'gradient' | 'gradient-light' | 'solid'
  colour?: 'black' | 'white'
  visualStyle?: 'line' | 'circle'
  icon?: IconName
  title?: string
}

const props = withDefaults(defineProps<RatingProps>(), {
  id: '',
  rating: 8,
  height: 'thin',
  theme: 'gradient',
  colour: 'black',
  visualStyle: 'line',
  icon: 'arrow',
  title: 'Rating',
})

if (
  !['solid', ''].includes(props.theme) &&
  !props.id &&
  props.visualStyle === 'circle'
) {
  console.warn(
    `Ratings require a unique :id. The gradient
    on the rating may not display properly.`)
}

const computedLineWidth = computed(
  () => Math.max(0, Math.min(10, props.rating)) * 10,
)

const gradientId = computed(() => {
  let name = '#VisionRating__linear-' + props.theme

  if (props.id) {
    name += '-' + props.id
  }

  return `url(${name})`
})
</script>

<style lang="postcss" scoped>
.VisionRating {
  --vision-rating-fill: var(
    --vision-rating-fill-override,
    theme('colors.black')
  );
  --vision-rating-bg-fill: var(
    --vision-rating-bg-fill-override,
    theme('colors.grey.400')
  );
  --vision-rating-height: var(
    --vision-rating-height-override,
    theme('height.1')
  );
  container: rating / inline-size;
}

/* VisionRating Line */
.VisionRating__line-inner {
  @apply w-full items-center flex gap-1 h-[var(--vision-rating-height)];

  &::before,
  &::after {
    @apply content-[''] transition-[width] duration-300 rounded-full;
  }

  &::before {
    @apply h-full;
    background: var(--vision-rating-fill);
    width: var(--rating-width);
  }

  &::after {
    @apply h-1/4 min-h-[2px]
    flex-shrink bg-[var(--vision-rating-bg-fill)];
    width: calc(100% - var(--rating-width) - 4px);
  }
}

.VisionRating__text {
  @apply flex justify-center font-fragment-mono text-xxs;
}

.VisionRating__title {
  @apply hidden text-grey-600;
}

/* VisionRating Circle */
.VisionRating__circle-container {
  @apply relative;
  --percentage-full: clamp(10, calc(var(--circle-rating) * 10), 95);
  --percentage-empty: calc(100 - var(--percentage-full));
  --bg-line-offset: 8;
  --dash-offset: 23;
  --rating-stroke: 5%;
  --bg-stroke: 1.5%;

  .VisionRating--height-regular & {
    --rating-stroke: 7%;
    --bg-stroke: 4%;
  }
}

.VisionRating__circle-background,
.VisionRating__circle-gradient-stroke {
  fill: none;
  stroke-linecap: round;
  transition:
    stroke-dashoffset 0.4s linear,
    stroke-dasharray 0.4s linear;
}

.VisionRating__circle-background {
  stroke: var(--vision-rating-bg-fill);
  stroke-dashoffset: calc(
    var(--dash-offset) + var(--percentage-empty) - var(--bg-line-offset)
  );
  stroke-dasharray: calc(var(--percentage-empty) - var(--bg-line-offset))
    calc(var(--percentage-full) + calc(var(--bg-line-offset) / 2));
  stroke-width: var(--bg-stroke);
}

.VisionRating__circle-gradient-stroke {
  stroke: var(--vision-rating-fill);
  stroke-dashoffset: var(--dash-offset);
  stroke-dasharray: var(--percentage-full) var(--percentage-empty);
  stroke-width: var(--rating-stroke);
}

.VisionRating__icon-container {
  @apply absolute inset-0 p-[35%];
}

/**
Container Queries
*/
@container rating (min-width: 80px) {
  .VisionRating--style-line {
    .VisionRating__text {
      @apply mt-2 justify-between;
    }
  }
  .VisionRating__title {
    @apply inline;
  }

  .VisionRating__circle-container {
    --rating-stroke: 4%;
    --bg-stroke: 1%;
    --bg-line-offset: 6;

    .VisionRating--height-regular & {
      --rating-stroke: 6%;
      --bg-stroke: 1.5%;
    }
  }
}

@container rating (min-width: 120px) {
  .VisionRating__circle-container {
    --rating-stroke: 3%;
    --bg-stroke: 1%;
    --bg-line-offset: 6;

    .VisionRating--height-regular & {
      --rating-stroke: 5%;
      --bg-stroke: 1.5%;
    }
  }
}

@container rating (min-width: 160px) {
  .VisionRating__circle-container {
    --rating-stroke: 2.5%;
    --bg-stroke: 0.5%;
    --bg-line-offset: 4;

    .VisionRating--height-regular & {
      --rating-stroke: 4%;
      --bg-stroke: 1%;
    }
  }

  .VisionRating__icon-container {
    @apply p-[40%];
  }
}

/**
MODIFIERS
--style
--theme
--colour
--height
*/

/* Visual Style */
.VisionRating--style-line {
  .VisionRating__text {
    @apply mt-1;
  }
}

/* Theme */
.VisionRating--theme-gradient {
  --vision-rating-gradient-stop-one: theme('colors.purple');
  --vision-rating-gradient-stop-two: theme('colors.blue');
  --vision-rating-gradient-stop-three: theme('colors.blue-light');

  .VisionRating__line-inner::before {
    @apply bg-gradient;
  }

  .VisionRating__circle-gradient-stroke {
    stroke: v-bind(gradientId);
  }
}

.VisionRating--theme-gradient-light {
  --vision-rating-gradient-stop-one: #ae61ea;
  --vision-rating-gradient-stop-two: #526eff;
  --vision-rating-gradient-stop-three: #46bcff;

  .VisionRating__line-inner::before {
    @apply bg-gradient-light;
  }

  .VisionRating__circle-gradient-stroke {
    stroke: v-bind(gradientId);
  }
}

/* Colour */
.VisionRating--colour-white {
  --vision-rating-fill: var(
    --vision-rating-fill-override,
    theme('colors.white')
  );
  --vision-rating-bg-fill: var(
    --vision-rating-bg-fill-override,
    theme('colors.grey.700')
  );

  .VisionRating__text {
    @apply text-white;
  }

  .VisionRating__title {
    @apply text-grey;
  }
}

.VisionRating--height-regular {
  .VisionRating__line-inner {
    @apply h-2;
  }
}
</style>
